import currency from "currency.js";
import { UNKNOWN_DISPLAY } from "../../utils/unknownNumbers";
import {
  GetAccount200Response,
  GetMargin200Response,
  GetMarkets200Response,
  GetOrders200Response,
  SideResponse,
  Stop,
} from "../../codegen-api";
import { FormValidatorKeysEnum } from "../../enums/form";
import { passesMarkCheck } from "../../utils/markGuards";
import { IMarketPrecisionGetter } from "../../contexts/MarketInstrumentContext/types";
import { IPositionInfo, ITradeInfo } from "../../interfaces/TradeInfo";
import { AssetResponse } from "../../utils/asset";

export function buildTradeInfo({
  order,
  isOption,
  totalValue,
  getMarketPrecision,
  asset,
  instrument,
  notEnoughBalanceError,
  orderValueTooSmall,
  marginData,
  t,
}: {
  order?: GetOrders200Response;
  isOption: boolean;
  totalValue: number;
  getMarketPrecision: IMarketPrecisionGetter;
  asset?: AssetResponse;
  instrument?: GetMarkets200Response;
  notEnoughBalanceError: boolean;
  orderValueTooSmall: boolean;
  marginData?: GetMargin200Response;
  t: (key: any, options?: any) => any;
}): ITradeInfo[] {
  if (!order) {
    return [];
  }

  // If is option, buy/sell shows different info
  if (isOption) {
    if (order.side === SideResponse.Buy) {
      // LIMIT BUY ORDER
      return [
        {
          title: t("total"),
          value: totalValue
            ? currency(totalValue, {
                precision: getMarketPrecision(
                  asset,
                  instrument?.instrument_type
                ).price_precision,
              }).format()
            : "---",
          warningOrError:
            notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
        },
        {
          title: t("amount_filled"),
          value: `${order.filled} / ${order.amount}`,
        },
      ];
    }

    return [
      {
        title: t("premiums_earned"),
        value: totalValue
          ? currency(totalValue, {
              precision: getMarketPrecision(
                asset,
                instrument?.instrument_type
              ).price_precision,
            }).format()
          : "---",
      },
      {
        title: t("margin_required"),
        value: marginData?.initial_margin
          ? currency(marginData?.initial_margin).format()
          : "---",
        warningOrError:
          notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
      },
      {
        title: t("amount_filled"),
        value: `${order.filled} / ${order.amount}`,
      },
    ];
  }

  // If not option, just return total and amount
  return [
    {
      title: t("total"),
      value: totalValue
        ? currency(totalValue, {
            precision: getMarketPrecision(asset, instrument?.instrument_type)
              .price_precision,
          }).format()
        : "---",
      warningOrError:
        notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
    },
  ];
}

export function getTriggerPriceValidateFn({
  order,
  instrument,
}: {
  order?: GetOrders200Response;
  instrument?: GetMarkets200Response;
}) {
  if (!order?.stop) {
    return {
      [FormValidatorKeysEnum.triggerPriceBelowMark]: () => true,
      [FormValidatorKeysEnum.triggerPriceAboveMark]: () => true,
    };
  }

  // If BUY STOP LOSS or SELL TAKE PROFIT, trigger price must be higher than mark
  if (
    (order?.side === SideResponse.Buy && order?.stop === Stop.StopLoss) ||
    (order?.side === SideResponse.Sell && order?.stop === Stop.TakeProfit)
  ) {
    return {
      [FormValidatorKeysEnum.triggerPriceBelowMark]: () => true,
      // Unknown mark (null): the check cannot run. Comparing with 0 passed
      // every "above" and failed every "below".
      [FormValidatorKeysEnum.triggerPriceAboveMark]: (v: string) =>
        passesMarkCheck(v, instrument?.mark_price, "atOrAbove"),
    };
  }
  // If SELL STOP LOSS or BUY TAKE PROFIT, trigger price must be lower than mark
  return {
    [FormValidatorKeysEnum.triggerPriceBelowMark]: (v: string) =>
      passesMarkCheck(v, instrument?.mark_price, "atOrBelow"),
    [FormValidatorKeysEnum.triggerPriceAboveMark]: () => true,
  };
}

export function buildPositionInfo({
  order,
  accountData,
  isOption,
  t,
  asset,
  buyingPower,
  notEnoughBalanceError,
  marginData,
}: {
  order?: GetOrders200Response;
  accountData?: GetAccount200Response;
  isOption: boolean;
  t: (key: any, options?: any) => any;
  asset?: AssetResponse;
  buyingPower: number;
  notEnoughBalanceError: boolean;
  marginData?: GetMargin200Response;
}): IPositionInfo[] {
  if (!order) {
    return [];
  }
  const currentPosition = (accountData?.positions ?? []).find(
    (position) => position.instrument_name === order.instrument_name
  );

  const buyingPowerInfo: IPositionInfo | undefined = !isOption
    ? {
        title: t("buying_power"),
        // 0 is this figure's "unknown" — an unvalued account or an unknown fee
        // schedule — and must not read as $0.00 of buying power.
        value: buyingPower > 0 ? currency(buyingPower).format() : UNKNOWN_DISPLAY,
        warningOrError: notEnoughBalanceError ? "error" : undefined,
        showErrorIcon: true,
      }
    : undefined;

  const liquidationPriceInfo: IPositionInfo | undefined = !isOption
    ? {
        title: t("liquidation_price"),
        value: marginData?.liquidation_price
          ? currency(marginData.liquidation_price).format()
          : "---",
      }
    : undefined;

  return [
    {
      title: t("available_balance"),
      value: accountData?.available_balance
        ? currency(accountData.available_balance).format()
        : "---",
      side: undefined,
      warningOrError:
        notEnoughBalanceError && !buyingPowerInfo ? "error" : undefined,
      showErrorIcon: true,
    },
    ...(buyingPowerInfo ? [buyingPowerInfo] : []),
    {
      title: `${t("position")} (${asset})`,
      value: currentPosition
        ? Number(currentPosition.amount).toFixed(2)
        : "---",
      side: currentPosition?.side,
    },
    ...(liquidationPriceInfo ? [liquidationPriceInfo] : []),
  ];
}
